import React, { useState, useEffect, useRef } from 'react';
import { getStoredCredentials } from '@/services/keycloakAuthService';
import ENDPOINTS from '@/utils/endpoints';

interface AuthenticatedImageProps {
  src: string | null | undefined;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  onLoad?: () => void;
  onError?: () => void;
}

/**
 * Builds the full avatar URL from a potentially relative path.
 * API returns paths like "/api/storage/avatars/abc.jpg" which need
 * the STORAGE_BASE URL prepended.
 *
 * Handles overlap: if the base URL already has a path prefix (e.g. /api)
 * and the src starts with the same prefix, we avoid duplication.
 */
const buildFullUrl = (src: string): string => {
  if (src.startsWith('http://') || src.startsWith('https://')) return src;
  const base = ENDPOINTS.STORAGE_BASE?.replace(/\/$/, '') || '';
  const path = src.startsWith('/') ? src : `/${src}`;

  // Detect overlapping path segments between base URL and src
  try {
    const parsed = new URL(base);
    const basePath = parsed.pathname.replace(/\/$/, '');
    if (basePath && basePath !== '/' && path.startsWith(`${basePath}/`)) {
      return `${parsed.origin}${path}`;
    }
  } catch {
    // base is not a full URL (e.g. relative proxy path), just concatenate
  }

  return `${base}${path}`;
};

export const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({
  src,
  alt,
  className,
  style,
  onLoad,
  onError,
}) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const objectUrlRef = useRef<string | null>(null);

  // Refs let us call the latest onLoad/onError without making them effect deps
  // (the effect should only re-run when `src` changes, not on every parent
  // re-render where the caller passes inline callbacks).
  const onLoadRef = useRef(onLoad);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onLoadRef.current = onLoad;
    onErrorRef.current = onError;
  }, [onLoad, onError]);

  useEffect(() => {
    if (!src) {
      setHasError(true);
      onErrorRef.current?.();
      return;
    }

    let cancelled = false;

    const fetchImage = async () => {
      try {
        const credentials = await getStoredCredentials();
        if (!credentials?.access_token) {
          throw new Error('No access token');
        }

        const fullUrl = buildFullUrl(src);
        const response = await fetch(fullUrl, {
          headers: {
            Authorization: `Bearer ${credentials.access_token}`,
          },
        });

        if (!response.ok) throw new Error('Failed to load image');

        const blob = await response.blob();
        if (!cancelled) {
          const url = URL.createObjectURL(blob);
          objectUrlRef.current = url;
          setObjectUrl(url);
          setHasError(false);
          onLoadRef.current?.();
        }
      } catch {
        if (!cancelled) {
          setHasError(true);
          onErrorRef.current?.();
        }
      }
    };

    void fetchImage();

    return () => {
      cancelled = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [src]);

  if (hasError || !objectUrl) return null;

  return <img src={objectUrl} alt={alt} className={className} style={style} />;
};
